import { Link } from "react-router-dom";
import { ArrowRight, Bone, HeartHandshake, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES, STATS } from "@/data/content";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Hygiene first, always",
    text: "Specialised pup-friendly flooring, pet-safe products and vet-supervised protocols keep the facility spotless and safe.",
  },
  {
    icon: HeartHandshake,
    title: "People who get dogs",
    text: "Trained staff coached by top trainers, with regular vet visits for thorough checkups.",
  },
  {
    icon: Bone,
    title: "Room for every temperament",
    text: "Multiple breakout areas mean shy, senior and super-social pups each get a space designed for them.",
  },
  {
    icon: Sparkles,
    title: "Days full of joy",
    text: "Supervised play, socialisation, enrichment treats and freshly cooked meals — happiness is the house standard.",
  },
];

export default function About() {
  return (
    <>
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <Reveal y={14}>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
                <span className="h-px w-10 bg-bark" aria-hidden="true" />
                About us
              </p>
            </Reveal>
            <h1 className="mt-6 font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              <span className="mask-line">
                <span style={{ animationDelay: "0.15s" }}>A facility built around</span>
              </span>
              <span className="mask-line">
                <span style={{ animationDelay: "0.28s" }}>
                  <em className="font-editorial italic text-bark">pups, not cages.</em>
                </span>
              </span>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                We began with a simple question: what would a day care and boarding centre look like
                if it were designed while keeping pups and their needs in mind?
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                The answer is a space with specialised flooring chosen for pups' wellbeing, trained
                staff guided by top trainers, regular vet visits, pet-safe everything, and breakout
                zones for dogs of every temperament. Today, we're proud to be known as India's most
                trusted pet care brand.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <Link
                to="/services"
                data-testid="about-cta-services"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink"
              >
                See what we offer
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.3}>
            <img
              src={IMAGES.playGroup}
              alt="Dogs playing together in a supervised breakout area"
              className="aspect-[4/5] w-full rounded-t-[12rem] rounded-b-[2rem] object-cover ring-1 ring-ink/10 sm:aspect-square"
            />
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Reveal>
          <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
            <span className="h-px w-10 bg-bark" aria-hidden="true" />
            What we stand for
          </p>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
            The values behind <em className="font-editorial italic text-bark">every wag</em>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <Reveal key={v.title} delay={i * 0.07}>
                <div
                  data-testid={`about-value-${i + 1}`}
                  className="h-full rounded-3xl border border-ink/8 bg-white p-9 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_-28px_rgba(15,23,42,0.3)]"
                >
                  <span className="w-fit rounded-full bg-bark-soft p-3 text-bark block">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-navy py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-4 sm:px-6 lg:grid-cols-4 lg:px-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.07}>
              <div data-testid={`about-stat-${i + 1}`} className="text-center">
                <p className="font-display text-5xl font-medium tracking-tight text-cream">
                  {s.value}
                </p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.24em] text-cream/50">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
